<template>
  <section>
    <AppLoading v-if="filteredAssignments.length === 0" />
    <template v-else>
      <p class="mb-3">{{ $t('employee.assignments.introText', { count: filteredAssignments.length }) }}</p>
      <b-table
        primary-key="name"
        :items="filteredAssignments"
        :fields="fields"
        table-class="table-minimal"
        :empty-text="$t('common.noRecordsToShow')"
        show-empty
      >
        <template #cell(profile)="data">
          <b-button
            class="border-0"
            size="sm"
            @click="() => redirectToAssignmentProfile(data.item.employee_id)"
            :variant="data.item.type === 'Contract' ? 'outline' : 'outline-primary'"
            :disabled="data.item.type === 'Contract'"
          >
            <font-awesome-icon :icon="['fad', 'user']" />
          </b-button>
        </template>
        <template #cell(full_name)="data">
          {{ data.item.full_name }}
        </template>
        <template #cell(employee_code)="data">
          {{ data.item.employee_code }}
        </template>
        <template #cell(payschedule)="data">
          {{ data.item.payschedule }}
        </template>
        <template #cell(contract)="data">
          {{ data.item.contract }}
        </template>
        <template #cell(current_state)="data">
          {{ $t(EMPLOYEE_STATUS_LABEL_KEYS[convertBackendStatusToDisplayStatus(data.item)]) }}
        </template>
      </b-table>
    </template>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router/composables';
import AppLoading from '@/components/AppLoading.vue';
import i18n from '@/i18n';
import { EMPLOYEE_STATUS_LABEL_KEYS, convertBackendStatusToDisplayStatus } from '@/utils/Employee';

const $router = useRouter();

const fields = computed(() => [
  {
    key: 'profile',
    label: i18n.t('employee.assignments.columns.profile'),
  },
  {
    key: 'full_name',
    label: i18n.t('employee.assignments.columns.name'),
  },
  {
    key: 'employee_code',
    label: i18n.t('employee.assignments.columns.employeeCode'),
  },
  {
    key: 'payschedule',
    label: i18n.t('employee.assignments.columns.payschedule'),
  },
  {
    key: 'type',
    label: i18n.t('employee.assignments.columns.type'),
    formatter: value =>
      value === 'Contract'
        ? i18n.t('employee.assignments.typeContract')
        : i18n.t('employee.assignments.typeAssignment'),
  },
  {
    key: 'contract',
    label: i18n.t('employee.assignments.columns.contract'),
  },
  {
    key: 'current_state',
    label: i18n.t('employee.assignments.columns.status'),
  },
]);

const props = defineProps({
  employee: {
    type: Object,
    required: true,
  },
  payschedules: {
    type: Array,
    required: true,
  },
  otherAssignments: {
    type: Array,
    required: true,
  },
});

const filteredAssignments = computed(() => {
  const assignments = [];
  for (const assignment of props.otherAssignments) {
    const payschedule =
      props.payschedules.find(paySchedule => paySchedule.pay_schedule_id === assignment.pay_schedule_id)?.name ||
      i18n.t('employee.assignments.notAvailable');
    let type = 'Assignment';
    let fullName = assignment.full_name;
    let employeeCode = assignment.employee_code;
    let currentState = assignment.current_state;
    const contract = assignment.properties?.contract_type || i18n.t('employee.assignments.notAvailable');
    if (assignment.employee_contract_id) {
      type = 'Contract';
      fullName = props.employee.full_name;
      employeeCode = props.employee.employee_code;
      if (assignment.properties.inactive || assignment.ended_on) {
        currentState = 'DISABLED';
      } else {
        currentState = props.employee.current_state;
      }
    }
    assignments.push({
      type: type,
      employee_contract_id: assignment.employee_contract_id,
      employee_id: assignment.employee_id,
      payschedule: payschedule,
      full_name: fullName,
      employee_code: employeeCode,
      current_state: currentState,
      contract: contract,
    });
  }
  return assignments;
});

const redirectToAssignmentProfile = employeeId => {
  $router.push({
    name: 'employee',
    params: {
      employeeId: employeeId,
    },
  });
};
</script>
